import { createContext, useEffect, useState } from 'react'
import req from '@sportninja/common/api/request'
// @ts-ignore
import { store } from '@sportninja/common/store'
import { Country } from '@sportninja/common/hooks/useCountries'

/**
 * Sorts countries so Canada appears first, then US, then the rest.
 */
function sortWithPriority(countries: Country[]): Country[] {
  const ca = countries.find((c) => c.iso_3166_2 === 'CA')
  const us = countries.find((c) => c.iso_3166_2 === 'US')
  const rest = countries.filter(
    (c) => c.iso_3166_2 !== 'CA' && c.iso_3166_2 !== 'US'
  )
  return [...(ca ? [ca] : []), ...(us ? [us] : []), ...rest]
}

/**
 * Reads countries from Redux store (populated on app init).
 * Falls back to API call if store is not yet populated.
 */
async function getCountryList(): Promise<Country[]> {
  try {
    const reduxCountries = store?.getState()?.types?.countries
    if (reduxCountries && Object.keys(reduxCountries).length > 0) {
      return sortWithPriority(Object.values(reduxCountries))
    }
    // Fallback to direct API call if Redux state is not yet populated
    const response = await req('/countries')
    return sortWithPriority(response)
  } catch (e) {
    console.error(e)
    return []
  }
}

export const CountryContext = createContext<Country[]>([])

export function CountryProvider({ children }: { children: React.ReactNode }) {
  const [countryList, setCountryList] = useState<Country[]>([])

  async function fetchCountryList() {
    const list = await getCountryList()
    setCountryList(list)
  }

  useEffect(() => {
    if (countryList.length < 1) {
      fetchCountryList()
    }
  }, [])

  return (
    <CountryContext.Provider value={countryList}>
      {children}
    </CountryContext.Provider>
  )
}
